import {Component, type ErrorInfo, type ReactNode} from 'react';
import {cn} from '@aiw/ui';

/**
 * A crash must never be a blank screen.
 *
 * Jacky, 17 September, after the drawer's release: *"no error, click on drawer gets me blank
 * screen"* — and that is the worst possible report, because it carries no information at all. React
 * unmounts the whole tree when a render throws and nothing catches it, so ONE bad value in ONE
 * component takes the entire CRM away and leaves white. The page cannot say what happened, and
 * neither can he.
 *
 * This boundary is deliberately BELOW the router and ABOVE every page and the EmmA drawer, so the
 * failure is contained to the panel that failed: the rail and the top bar stay, the message names the
 * error, and the button reloads. Nothing here is a fallback UI for a business case — there is no such
 * thing — it exists so that a defect is legible instead of silent.
 *
 * No `useT()` HERE, and that is on purpose: this class sits above the language provider, so the
 * wording is written in both languages rather than resolved through the mechanism that might itself
 * be what threw.
 */
type Props = {children: ReactNode; /** What failed, for the message — "this screen", "her drawer". */ where?: string};
type State = {error: Error | null; componentStack: string | null};

export class AppError extends Component<Props, State> {
  state: State = {error: null, componentStack: null};

  static getDerivedStateFromError(error: Error): Partial<State> {
    return {error};
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Keep the component stack: the message alone rarely says WHICH list item or field threw, and
    // this is the one place that knows.
    this.setState({componentStack: info.componentStack ?? null});
    // Also to the console, so a screenshot of devtools is enough to diagnose it.
    console.error('[AIW] render failed', error, info.componentStack);
  }

  render(): ReactNode {
    const {error, componentStack} = this.state;
    if (!error) return this.props.children;
    return (
      <div role="alert" className="m-4 rounded-lg border border-warning bg-card p-4 text-sm">
        <p className="font-semibold">
          This screen hit an error — nothing was changed. 这个页面出错了——没有做任何更改。
        </p>
        <p className="mt-1 text-muted-foreground">
          {this.props.where ?? 'The view'} failed to render. Nothing was saved, sent or changed.
          {' '}重新加载即可继续；如果仍然出错，把下面这一行发给我。
        </p>
        <pre className={cn('mt-3 max-h-40 overflow-auto rounded bg-muted p-2 font-mono text-micro')}>
          {error.message}
          {componentStack ? `\n${componentStack.trim().split('\n').slice(0, 6).join('\n')}` : ''}
        </pre>
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="mt-3 min-h-11 rounded-md bg-primary px-4 text-sm text-primary-foreground"
        >
          Reload / 重新加载
        </button>
      </div>
    );
  }
}
